/**
 * @module payment-flow-storage
 * @description This module centralizes all interactions with sessionStorage
 * for the payment flow. It provides a clean API for getting, setting, and
 * clearing persisted data related to payments.
 */
import { Currency } from '@seapoint/server/enums';
import { sessionStorageService } from '../../utils/session-storage-service';
import { BillPayFlow } from './bill-pay-flow/components/bill-pay-flows-wrapper';
import { BillPayEntryPoint, PaymentAction } from './use-payment-flow';
import { isBillPayEntryPoint } from './utils/is-bill-pay-entry-point';
import { isBillPayFlow } from './utils/is-bill-pay-flow';
import { isPaymentAction } from './utils/is-payment-action';

export const PAYMENT_ACTION_PARAM_KEY = 'action';
const PAYMENT_DETAILS_KEY = 'paymentDetails';
const SELECTED_BENEFICIARY_ID_KEY = 'beneficiaryId';
const UPLOADED_DOCUMENT_DETAILS_KEY = 'uploadedDocumentDetails';
const BILL_PAY_FLOW_KEY = 'billPayFlow';
const SHOULD_RENDER_REVIEW_STEP_KEY = 'shouldRenderReviewStep';
const EVIDENCE_ID_KEY = 'uploadedEvidenceId';
const EVIDENCE_PREFILL_KEY = 'evidencePrefill';
const BILL_PAY_ENTRY_POINT_KEY = 'billPayEntryPoint';

export type PaymentDetails = {
  sourceAccountId: string;
  sourceAccountName?: string;
  sourceAccountLogo?: string;
  destinationAccountName?: string;
  destinationAccountLogo?: string;
  amount: number;
  currency?: Currency;
  reference: string;
  beneficiaryId?: string;
  isInstantTransfer?: boolean;
};

export type DocumentDetails = {
  fileName: string;
  documentUrl: string;
};

export type EvidencePrefill = {
  beneficiaryId?: string | null;
  reference?: string;
};

export const paymentActionStore = {
  get: (): PaymentAction | null => {
    const stored = sessionStorageService.getString(PAYMENT_ACTION_PARAM_KEY);

    return isPaymentAction(stored) ? stored : null;
  },
  update: (paymentAction: PaymentAction) => {
    sessionStorageService.set(PAYMENT_ACTION_PARAM_KEY, paymentAction);
  },
  clear: () => {
    sessionStorageService.remove(PAYMENT_ACTION_PARAM_KEY);
  },
};

export const billPayFlowStore = {
  get: (): BillPayFlow | null => {
    const stored = sessionStorageService.getString(BILL_PAY_FLOW_KEY);

    return isBillPayFlow(stored) ? stored : null;
  },
  update: (billPayFlow: BillPayFlow) => {
    sessionStorageService.set(BILL_PAY_FLOW_KEY, billPayFlow);
  },
  clear: () => {
    sessionStorageService.remove(BILL_PAY_FLOW_KEY);
  },
};

export const shouldRenderReviewStepStore = {
  get: (): boolean => {
    return sessionStorageService.get(SHOULD_RENDER_REVIEW_STEP_KEY) ?? false;
  },
  update: (shouldRenderReviewStep: boolean) => {
    sessionStorageService.set(SHOULD_RENDER_REVIEW_STEP_KEY, JSON.stringify(shouldRenderReviewStep));
  },
  clear: () => {
    sessionStorageService.remove(SHOULD_RENDER_REVIEW_STEP_KEY);
  },
};

export const paymentDetailsStore = {
  get: (): PaymentDetails | null => {
    return sessionStorageService.get(PAYMENT_DETAILS_KEY);
  },
  update: (paymentDetails: PaymentDetails) => {
    sessionStorageService.set(PAYMENT_DETAILS_KEY, JSON.stringify(paymentDetails));
  },
  clear: () => {
    sessionStorageService.remove(PAYMENT_DETAILS_KEY);
  },
};

export const documentDetailsStore = {
  get: (): DocumentDetails | null => {
    return sessionStorageService.get(UPLOADED_DOCUMENT_DETAILS_KEY);
  },
  update: (documentDetails: DocumentDetails) => {
    sessionStorageService.set(UPLOADED_DOCUMENT_DETAILS_KEY, JSON.stringify(documentDetails));
  },
  clear: () => {
    sessionStorageService.remove(UPLOADED_DOCUMENT_DETAILS_KEY);
  },
};

export const beneficiaryIdStore = {
  get: (): string | null => {
    return sessionStorageService.getString(SELECTED_BENEFICIARY_ID_KEY);
  },
  update: (beneficiaryId: string) => {
    sessionStorageService.set(SELECTED_BENEFICIARY_ID_KEY, beneficiaryId);
  },
  clear: () => {
    sessionStorageService.remove(SELECTED_BENEFICIARY_ID_KEY);
  },
};

export const uploadedEvidenceIdStore = {
  get: (): string | null => {
    return sessionStorageService.getString(EVIDENCE_ID_KEY);
  },
  update: (evidenceId: string) => {
    sessionStorageService.set(EVIDENCE_ID_KEY, evidenceId);
  },
  clear: () => {
    sessionStorageService.remove(EVIDENCE_ID_KEY);
  },
};

export const evidencePrefillStore = {
  get: (): EvidencePrefill | null => {
    return sessionStorageService.get(EVIDENCE_PREFILL_KEY);
  },
  update: (data: EvidencePrefill) => {
    sessionStorageService.set(EVIDENCE_PREFILL_KEY, JSON.stringify(data));
  },
  clear: () => {
    sessionStorageService.remove(EVIDENCE_PREFILL_KEY);
  },
};

export const billPayEntryPointStore = {
  get: (): BillPayEntryPoint | null => {
    const stored = sessionStorageService.getString(BILL_PAY_ENTRY_POINT_KEY);

    return isBillPayEntryPoint(stored) ? stored : null;
  },
  update: (billPayEntryPoint: BillPayEntryPoint) => {
    sessionStorageService.set(BILL_PAY_ENTRY_POINT_KEY, billPayEntryPoint);
  },
  clear: () => {
    sessionStorageService.remove(BILL_PAY_ENTRY_POINT_KEY);
  },
};
